import { debug, UA, URI, version as JSSIPVersion, WebSocketInterface } from '@wui/jssip'
import { RTCSession } from '@wui/jssip/lib/RTCSession'
import { IncomingRequest } from '@wui/jssip/lib/SIPMessage'
import { Subscriber } from '@wui/jssip/lib/Subscriber'
import { RTCSessionEvent } from '@wui/jssip/lib/UA'
import { computed, readonly, ref, shallowRef, triggerRef, watch } from 'vue'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { useCallsStore } from '@/entities/calls'
import { useStoreSettingsForwarding } from '@/entities/forwarding'
import { useStoreMediaDevices } from '@/entities/media-devices'
import { useStorePickupCall } from '@/entities/pickup-call'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useAutoAnswer } from '@/shared/composables/use-auto-answer'
import { useToggle } from '@/shared/composables/use-toggle'
import { useStoreEnvSettings } from '@/shared/store/environment'
import { appPackageVersion } from '@/shared/store/package-meta'
import { Room } from '@/shared/types/store'
import { isStageOrDevelopment, tauriMode } from '@/shared/utils/import-meta'
import { clearSessionInvitedByName, getAnswerOptions, getInvitedByName, parseCallMediaRequest, setSessionInvitedByName } from '@/shared/utils/jssip'
import { isOnCallSessionRoute } from '@/shared/utils/router'

import { call, callAsync, waitForIncomingSession, waitForSessionEstablished } from './call'
import { SIP_SUBSCRIBE_EXPIRES_DEFAULT_SECONDS } from './constants'
import { eventBus } from './event-bus'
import { onNewRTCSession } from './on-new-rtc-session'
import { onRegistrationFailed } from './on-registration-failed'
import { fireReplaceableCallReceiveEvent } from './on-replaceable-call-receive'
import { takePickupConnectedParty } from './pickup-connected-party'
import { register } from './register'
import { start } from './start'
import { stop } from './stop'
import { SetupUA } from './types'
import { userStatusEndedHandler } from './use-session-event-listener'
import { RTCSessionFacade, STATE, STATUS_CODES, useRTCSessionFacade } from './use-session-facade'

if (isStageOrDevelopment()) {
  // debug.enable('JsSIP:*')
  debug.disable()
} else {
  debug.disable()
}

let ua: UA
let confUri: URI | undefined
let callingYourself = false

const {
  enable: setRegistered,
  disable: setUnRegistered,
  value: registered,
} = useToggle(false)

const {
  enable: setConnected,
  disable: setDisconnected,
} = useToggle(false)

const { checkAutoAnswer } = useAutoAnswer()

const sessions = shallowRef<Map<string, RTCSessionFacade>>(new Map())
/** Сессии с autoAnswer (joinConference и т.п.) — не показываем как входящие плашки */
const autoAnsweringSessionIds = shallowRef(new Set<string>())
const ringingSessions = computed(() => {
  return Array.from(sessions.value.values()).filter(({ sessionState, sessionId }) => (
    sessionState.value === STATE.RINGING
    && !autoAnsweringSessionIds.value.has(sessionId)
  ))
})
const activeSessionId = ref<string>()
const registrationId = ref<string>()

const markAutoAnswering = (sessionId: string) => {
  const next = new Set(autoAnsweringSessionIds.value)
  next.add(sessionId)
  autoAnsweringSessionIds.value = next
}

const unmarkAutoAnswering = (sessionId: string) => {
  if (!autoAnsweringSessionIds.value.has(sessionId)) {
    return
  }
  const next = new Set(autoAnsweringSessionIds.value)
  next.delete(sessionId)
  autoAnsweringSessionIds.value = next
}

export const getUA = (): UA | undefined => {
  return ua
}

export const isRegistered = (): boolean => {
  return registered.value
}

const removeSession = (id: string) => {
  if (id === activeSessionId.value) {
    activeSessionId.value = undefined
  }

  if (sessions.value.has(id)) {
    if (sessions.value.size === 1) {
      sessions.value.forEach((session) => {
        const storeMediaDevices = useStoreMediaDevices()
        const audioStream = session.localAudioStream.value
        const videoStream = session.localVideoStream.value
        const localScreenStream = session.localScreenStream.value
        if (audioStream) {
          storeMediaDevices.closeStream(audioStream)
        }
        if (videoStream) {
          storeMediaDevices.closeStream(videoStream)
        }
        if (localScreenStream) {
          storeMediaDevices.closeStream(localScreenStream)
        }
      })

      userStatusEndedHandler(id)
    }

    sessions.value.delete(id)
    unmarkAutoAnswering(id)
    useCallSessionRoomStore().unbindSession(id)
    clearSessionInvitedByName(id)

    const sessionsArray = [...sessions.value.values()]
    const connectedSessions = sessionsArray.filter(s => s.sessionState.value === STATE.CONNECTED)
    // Один входящий без активного вызова → модалка; несколько → только плашки
    if (sessions.value.size > 0 && connectedSessions.length === 0) {
      const ringing = sessionsArray.filter((s) => (
        s.sessionState.value === STATE.RINGING
        && !autoAnsweringSessionIds.value.has(s.sessionId)
      ))
      if (ringing.length === 1) {
        ringing[0]?.dialog.showDialog()
      }
    }

    triggerRef(sessions)
  }
}

const readReplacesCallId = (headers?: Array<string>): string | undefined => {
  const header = headers?.find((item) => item.startsWith('replaces:'))
  const callId = header?.slice('replaces:'.length).trim()
  return callId || undefined
}

const addSession = (e: RTCSessionEvent) => {
  const { currentUser } = useCurrentUser()
  const sessionId = e.session.id
  const request = e.request as IncomingRequest & { replaces?: { call_id?: string }, extraHeaders?: Array<string>, call_id: string }
  callingYourself = e.originator === 'local' ? request.to.uri.user === currentUser.value?.internalNumber : request.from.uri.user === currentUser.value?.internalNumber
  if (e.originator === 'local') {
    setActiveSession(sessionId)
  }
  const replacesCallId = e.originator === 'local' ? readReplacesCallId(request.extraHeaders) : undefined
  const connectedParty = replacesCallId ? takePickupConnectedParty(replacesCallId) : undefined
  const sessionFacade = useRTCSessionFacade(e.session, request, connectedParty)
  if (e.originator === 'remote') {
    setSessionInvitedByName(sessionId, getInvitedByName(request))
  }
  const replaceCallId = request?.replaces?.call_id
  const extraHeader = request?.extraHeaders?.at(0) as string
  sessionFacade.referToSessionId.value = extraHeader?.includes('refer-call') ? extraHeader.replace(/"/gm, '').replace(/^refer-call:/gm, '') : null

  const { getSettingByKey } = useStoreEnvSettings()
  const autoAnswerVideoEnabled = getSettingByKey('autoAnswer')

  const sessionRoom = useCallSessionRoomStore().getRoom(sessionId)
  const roompServedUser = sessionRoom?.pServedUser
  const isToCheck = autoAnswerVideoEnabled
    ? { audio: true, video: true }
    : (roompServedUser ? checkAutoAnswer(roompServedUser) : false)

  const autoAnswer = (e.originator === 'remote' && roompServedUser && isToCheck)
    ? isToCheck
    : undefined

  // Подавляем плашку ДО sessions.set: progress уже мог выставить RINGING в facade
  if (autoAnswer) {
    markAutoAnswering(sessionId)
  }

  sessions.value.set(sessionId, sessionFacade)

  if (replaceCallId) {
    fireReplaceableCallReceiveEvent(replaceCallId)
  } else if (autoAnswer) {
    const { audio, video } = autoAnswer
    const stopAutoAnswerWatch = watch(() => sessionFacade.sessionState.value, (state) => {
      if (state === STATE.RINGING) {
        return
      }
      unmarkAutoAnswering(sessionId)
      stopAutoAnswerWatch()
    })
    void getAnswerOptions(audio, video, !!sessionRoom?.isGroup)
      .then((options) => sessionFacade.answer(options))
      .catch((error: unknown) => {
        console.error(error)
        unmarkAutoAnswering(sessionId)
        stopAutoAnswerWatch()
      })
  } else if (callingYourself) {
    if (e.originator === 'local') {
      // Исходящий самому себе
      sessionFacade.dialog.showDialog()
    } else if (e.originator === 'remote') {
      if (localStorage.getItem('callingYourself') === 'true') {
        // Вызов самому себе входящий
        removeSession(sessionId)
        sessionFacade.terminate()
        localStorage.removeItem('callingYourself')
      } else {
        // Вызов самому себе на другом устройстве
        sessionFacade.dialog.showDialog()
      }
    }
  } else {
    const otherSessions = Array.from(sessions.value.values()).filter((facade) => facade.sessionId !== sessionId)
    const hasConnectedOther = otherSessions.some((facade) => facade.sessionState.value === STATE.CONNECTED)
    const otherIncomingRinging = otherSessions.filter((facade) => facade.sessionState.value === STATE.RINGING)
    const hasOtherIncomingUi = otherSessions.some((facade) => (
      facade.sessionState.value === STATE.RINGING
      || facade.isCallingDialogOpened.value
    ))
    // Плашка только пока смотрим экран активного вызова; ушли в чат — модалка
    const viewingActiveCall = hasConnectedOther && isOnCallSessionRoute()
    const shouldShowAsPlaqueOnly = viewingActiveCall || hasOtherIncomingUi

    if (
      !sessionFacade.replaceOnProgress.value
      && e.originator === 'remote'
      && sessions.value.size > 1
      && (getCurrentSession() || hasConnectedOther)
    ) {
      forwardHandler(sessionFacade)
    }

    if (!sessionFacade.referToSessionId.value && !shouldShowAsPlaqueOnly) {
      sessionFacade.dialog.showDialog()
    }

    // >1 входящих: закрываем модалку у уже звонящих, все показываем плашками
    if (e.originator === 'remote' && otherIncomingRinging.length > 0) {
      otherIncomingRinging.forEach((facade) => {
        if (facade.isCallingDialogOpened.value) {
          facade.dialog.closeDialog.value?.(undefined)
        }
      })
    }
  }

  if (request.call_id) {
    sessionFacade.callId.value = request.call_id

    if (request.hasHeader('X-Satel-Params') && sessionRoom?.isGroup) {
      try {
        const r = parseCallMediaRequest(request.getHeader('X-Satel-Params'))
        const { setCallMediaRequest } = useCallsStore()
        setCallMediaRequest(request.call_id, r)
      } catch (error) {
        console.error('Failed to store call media request from X-Satel-Params', error)
      }
    }
  }

  triggerRef(sessions)
}

/*
  Получение текущего RTCSession
*/
const getCurrentSession = (): RTCSession | undefined => {
  if (!activeSessionId.value) return undefined
  const session = sessions.value.get(activeSessionId.value)
  return session?.session
}

/*
  Получение текущего RTCSessionFacade. undefined, если нет активной сессии
*/
const currentRTCSessionFacade = computed(() => {
  if (!activeSessionId.value) return undefined
  return sessions.value.get(activeSessionId.value)
})

/*
  Получение RTCSessionFacade по Session id
*/
const getRTCSessionFacadeById = (id: string): RTCSessionFacade | undefined => {
  return sessions.value.get(id)
}

const getReferRTCSessionFacade = (sessionId: string) => {
  for (const [, sessionFacade] of sessions.value) {
    if (sessionFacade.referToSessionId.value === sessionId) {
      return sessionFacade
    }
  }
  return null
}

const setActiveSession = (sessionId: string, options?: { holdOthers?: boolean }) => {
  activeSessionId.value = sessionId
  if (options?.holdOthers === false) {
    return
  }
  holdAllExcept(sessionId)
}

/** Не удерживать остальные сессии на ближайшем confirmed (для «удержать входящий и принять») */
let skipHoldOnNextConfirmed = false

const setSkipHoldOnNextConfirmed = () => {
  skipHoldOnNextConfirmed = true
}

const clearSkipHoldOnNextConfirmed = () => {
  skipHoldOnNextConfirmed = false
}

const holdAllExcept = (sessionId: string) => {
  for (const session of sessions.value.values()) {
    if (session.sessionId !== sessionId && session.sessionState.value === STATE.CONNECTED) {
      const sessionRoom = useCallSessionRoomStore().getRoom(session.sessionId)
      if (sessionRoom?.isGroup) {
        session.onHoldInConference()
      } else if (!session.isOnHold.value) {
        session.toggleHold()
      }
    }
  }
}

const forwardHandler = (sessionFacade: RTCSessionFacade) => {
  const storeSettingsForwarding = useStoreSettingsForwarding()
  const rule = storeSettingsForwarding.maxPriorityCallForwardingOnBusy
  const incomingNumber = sessionFacade.number
  if (rule) {
    const regex = new RegExp(rule.aNumber, 'gm')
    const result = incomingNumber.match(regex)
    if (result && result.length) {
      sessionFacade.terminate({ status_code: STATUS_CODES.BUSY_HERE })
    }
  }
}

const linkRoomToCallSessions = (room: Room) => {
  useCallSessionRoomStore().linkRoomToSessions(room)
}

const updateRoomInSessionIfNeed = (room: Room) => {
  useCallSessionRoomStore().linkRoomToSessions(room)
  triggerRef(sessions)
}

const userAgentNameHandler = () => {
  if (tauriMode()) {
    return `WebClientRTU-tauri: ${appPackageVersion}, JSSIP: ${JSSIPVersion}`
  } else if (window.matchMedia('(display-mode: standalone)').matches) {
    return `WebClientRTU-app: ${appPackageVersion}, JSSIP: ${JSSIPVersion}`
  } else {
    return `WebClientRTU-browser: ${appPackageVersion}, JSSIP: ${JSSIPVersion}`
  }
}

eventBus.on('removeSession', removeSession)
eventBus.on('addSession', addSession)
eventBus.on('confirmed', (sessionId) => {
  if (skipHoldOnNextConfirmed) {
    skipHoldOnNextConfirmed = false
    activeSessionId.value = sessionId
    return
  }
  setActiveSession(sessionId)
})
eventBus.on('unhold', (sessionId) => {
  setActiveSession(sessionId)
})

export const NO_ANSWER_TIMEOUT = 60

export function useWebRTC () {
  const init = (conf: SetupUA) => {
    confUri = URI.parse(conf.ua.uri)
    const socket = new WebSocketInterface(conf.sipWs)
    const isGuest = conf.ua?.guestToken
    ua = new UA({
      ...conf.ua,
      no_answer_timeout: NO_ANSWER_TIMEOUT,
      sockets: [socket],
      register: true,
      register_expires: 120,
      realm: 'debian',
      user_agent: userAgentNameHandler(),
    })
    if (isGuest) {
      ua.registrator().setExtraHeaders(['X-Satel-Auth-Token: ' + conf.ua.guestToken])
    }
    ua.addListener('registered', (e) => {
      const { response: { headers }} = e
      const callId = headers['Call-ID']
      if (callId && callId[0] && callId[0].parsed) {
        registrationId.value = callId[0].parsed
      }
      setRegistered()
    })
    ua.addListener('unregistered', setUnRegistered)
    ua.addListener('registrationFailed', onRegistrationFailed)
    ua.addListener('connected', () => {
      if (!isGuest) {
        useStorePickupCall().fetchAndSubscribePickupCalls()
      }
      setConnected()
    })
    ua.addListener('disconnected', setDisconnected)
    ua.addListener('newRTCSession', onNewRTCSession)
    ua.addListener('registrationExpiring', register)
  }

  const unregister = () => {
    if (!ua) return
    ua.unregister({
      all: true,
    })
  }

  const subscribe = (internalNumber: string): Subscriber => {
    if (!confUri) throw new Error(`uaConf is ${confUri}`)
    const ownInternalNumber = confUri.user.replace('$ROOT', '')
    const fromUri = new URI('sip', ownInternalNumber, confUri.host, confUri.port)

    const toUri = new URI('sip', internalNumber, fromUri.host, fromUri.port)
    const subscriber = ua.subscribe(
      `sip:${internalNumber}`,
      'presence',
      'application/dialog-info+xml', {
        expires: SIP_SUBSCRIBE_EXPIRES_DEFAULT_SECONDS,
        params: {
          from_uri: fromUri,
          to_uri: toUri,
        },
      })

    subscriber.subscribe()

    return subscriber
  }

  return {
    init,
    start,
    stop,
    register,
    unregister,
    subscribe,
    registered: readonly(registered),
    registrationId: readonly(registrationId),
    call,
    callAsync,
    waitForIncomingSession,
    waitForSessionEstablished,
    getCurrentSession,
    activeSessionId: readonly(activeSessionId),
    removeSession,
    getRTCSessionFacadeById,
    getReferRTCSessionFacade,
    setActiveSession,
    setSkipHoldOnNextConfirmed,
    clearSkipHoldOnNextConfirmed,
    sessions,
    ringingSessions,
    currentRTCSessionFacade,
    linkRoomToCallSessions,
    updateRoomInSessionIfNeed,
  }
}
